<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMarketFilters, type FiltersData, type MarketFilterItem } from './useMarketFilters'

const props = defineProps<{
  languagesList?: MarketFilterItem[]
  coursesList?: MarketFilterItem[]
  productTypesList?: MarketFilterItem[]
  holidaysList?: MarketFilterItem[]
}>()

const emit = defineEmits<{
  (event: 'filters-changed', data: FiltersData): void
}>()

const {
  selectedLanguage,
  searchText,
  isAuthored,
  isOwned,
  isAvailable,
  selectedCourses,
  selectedTypes,
  selectedHolidays,
  priceFrom,
  priceTo,
  currentSort,
  sorts
} = useMarketFilters(props, emit)

const showFiltersModal = ref(false)
const mobileSorts = computed(() => {
  return Object.values(sorts).reduce<Array<{ value: string; text: string }>>((items, sort) => {
    items.push({ value: sort.desc, text: `${sort.text} ↓` })
    items.push({ value: sort.asc, text: `${sort.text} ↑` })
    return items
  }, [])
})

const filtersCount = computed(() => {
  let count = 0
  count += [
    selectedLanguage.value,
    isAuthored.value,
    isOwned.value,
    isAvailable.value,
    priceFrom.value,
    priceTo.value
  ].reduce((acc, value) => acc + Number(Boolean(value)), 0)

  count += [selectedCourses.value, selectedTypes.value, selectedHolidays.value].reduce(
    (acc, value) => acc + value.length,
    0
  )

  return count
})
</script>

<template>
  <div class="market-filters-mobile">
    <v-select
      v-model="selectedLanguage"
      :label="selectedLanguage ? null : 'Все языки'"
      :items="languagesList"
    ></v-select>

    <v-search-panel v-model="searchText" class="mt-2" placeholder="Товар или ФИО автора"></v-search-panel>

    <v-button class="mt-1" mode="large flat block" @click="showFiltersModal = true">
      Фильтры
      <span class="blue-text ml-1">{{ filtersCount }}</span>
    </v-button>

    <v-select v-model="currentSort" class="mt-1" placeholder="Выберите сортировку" :items="mobileSorts"></v-select>

    <v-rich-modal v-model="showFiltersModal" title="Фильтры">
      <v-field>
        <template #label><p class="text-body-bold">Стоимость</p></template>

        <div class="d-flex">
          <v-input v-model="priceFrom" placeholder="100">
            <template #prepend>от&nbsp;</template>
          </v-input>

          <v-input v-model="priceTo" class="ml-2" placeholder="100 000">
            <template #prepend>до&nbsp;</template>
          </v-input>
        </div>
      </v-field>

      <v-field class="mt-3">
        <template #label><p class="text-body-bold">Курс</p></template>
        <v-select
          v-model="selectedCourses"
          placeholder="Выберите курс"
          :items="coursesList"
          multiselect
          :label="selectedCourses.length ? `Курс (${selectedCourses.length})` : null"
        ></v-select>
      </v-field>

      <v-field class="mt-3">
        <template #label><p class="text-body-bold">Тип товара</p></template>
        <v-select
          v-model="selectedTypes"
          placeholder="Выберите тип товара"
          :items="productTypesList"
          multiselect
          :label="selectedTypes.length ? `Тип товара (${selectedTypes.length})` : null"
        ></v-select>
      </v-field>

      <v-field class="mt-3">
        <template #label><p class="text-body-bold">Статус</p></template>

        <div class="d-flex flex-column">
          <v-checkbox v-model="isAuthored">Мои товары</v-checkbox>
          <v-checkbox v-model="isOwned" class="mt-1">Куплено</v-checkbox>
          <v-checkbox v-model="isAvailable" class="mt-1">Доступные</v-checkbox>
        </div>
      </v-field>

      <v-field class="mt-3">
        <template #label><p class="text-body-bold">Праздник</p></template>
        <v-select
          v-model="selectedHolidays"
          placeholder="Выбрите праздник"
          :items="holidaysList"
          multiselect
          :label="selectedHolidays.length ? `Праздник (${selectedHolidays.length})` : null"
        ></v-select>
      </v-field>

      <template #actions>
        <v-button mode="large primary" @click="showFiltersModal = false"> Применить фильтры </v-button>
      </template>
    </v-rich-modal>
  </div>
</template>

<style lang="scss">
.market-filters-desktop {
  display: flex;
  flex-direction: column;
}
</style>
